import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { toast } from "sonner";

import { dealsQueryKeys } from "@/hooks/useDeals";
import { reportsQueryKeys } from "@/hooks/useReports";
import { IAPIResponse } from "@/services/api.types";
import {
  getDealCompensationService,
  getProjectedCompensationService,
  putDealCompensationService,
} from "@/services/compensation.service";
import type { DealCompensation, ProjectedCompensation, ProjectedCompensationParams } from "@/types/compensation.types";

export const compensationQueryKeys = {
  // Under the deal's own key on purpose: `usePatchDeal` already invalidates
  // `dealsQueryKeys.detail(dealId)`, so a price change re-reads the amount.
  deal: (dealId: string) => [...dealsQueryKeys.detail(dealId), "compensation"] as const,
  projected: (params: ProjectedCompensationParams) => [...reportsQueryKeys.all(), "projected-compensation", params] as const,
};

export const useGetDealCompensation = (dealId: string) =>
  useQuery<DealCompensation, Error>({
    queryKey: compensationQueryKeys.deal(dealId),
    queryFn: () => getDealCompensationService(dealId),
    enabled: !!dealId,
  });

export const usePutDealCompensation = () => {
  const queryClient = useQueryClient();
  return useMutation<IAPIResponse<DealCompensation>, AxiosError, { dealId: string; pct: number | null }>({
    mutationFn: ({ dealId, pct }) => putDealCompensationService(dealId, pct),
    onSuccess: (res, { dealId }) => {
      if (res.data) queryClient.setQueryData(compensationQueryKeys.deal(dealId), res.data);
      queryClient.invalidateQueries({ queryKey: [...reportsQueryKeys.all(), "projected-compensation"] });
    },
    onError: err => toast.error(err.message),
  });
};

export const useGetProjectedCompensation = (params: ProjectedCompensationParams) =>
  useQuery<ProjectedCompensation, Error>({
    queryKey: compensationQueryKeys.projected(params),
    queryFn: () => getProjectedCompensationService(params),
    placeholderData: previous => previous,
  });
